import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Grid, Stack, styled } from '@mui/material'
import dayjs from 'dayjs'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useWatch } from 'react-hook-form'
import { ControlledMuiTextField } from 'components/general/forms/rhf/controlled-mui-textfield'
import { LoadingButton } from '@mui/lab'
import ModalsWrapper from 'components/general/dialogs/dialog-wrapper'
import { AddInterestPeriodRequest } from 'types/interest-periods/interest-periods-requests'
import { useCreateInterestPeriodMutation } from 'redux/apis/interest-rates-api'
import { ControlledMuiDateField } from 'components/general/forms/rhf/controlled-mui-date'
import { createInterestRateForm } from 'utils/validators/interest-period'
import { toast } from 'react-toastify'
import { isServerError } from 'utils/error-utilts'

const FORM_ID = 'create-interest-period'

type AddInterestPeriodDialogProps = {
    isOpen: boolean
    onClose: () => void
}

const getDefaultValues = (): AddInterestPeriodRequest => {
    const now = dayjs()
    const currentYear = now.year()
    const julyFirstThisYear = dayjs().year(currentYear).month(6).date(1).startOf('day')

    let startDateDayjs: dayjs.Dayjs
    let endDateDayjs: dayjs.Dayjs

    if (now.isBefore(julyFirstThisYear)) {
        // If current date is before July 1st of current year
        startDateDayjs = dayjs().year(currentYear).month(0).date(1).startOf('day')
        endDateDayjs = dayjs().year(currentYear).month(5).endOf('month')
    } else {
        // If current date is on or after July 1st of current year
        startDateDayjs = julyFirstThisYear
        endDateDayjs = dayjs().year(currentYear).month(11).date(31).startOf('day') // December 31st
    }

    return {
        annualInterestRatePercentage: 10,
        startDate: startDateDayjs.toDate(),
        endDate: endDateDayjs.toDate(),
    }
}

export const AddInterestPeriodDialog: React.FC<AddInterestPeriodDialogProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation()

    const [create, { isLoading }] = useCreateInterestPeriodMutation()

    const { handleSubmit, control } = useForm<AddInterestPeriodRequest>({
        defaultValues: getDefaultValues(),
        shouldFocusError: true,
        mode: 'onSubmit',
        shouldUnregister: true,
        resolver: zodResolver(createInterestRateForm(t)),
    })

    const selectedStartDate = useWatch({ control, name: 'startDate' })

    const onSubmit = async (values: AddInterestPeriodRequest) => {
        try {
            await create({
                annualInterestRatePercentage: values.annualInterestRatePercentage,
                startDate: dayjs(values.startDate).format('YYYY-MM-DD'),
                endDate: dayjs(values.endDate).format('YYYY-MM-DD'),
            }).unwrap()

            toast(t('TEXT_SUCCESS_FOR_INTEREST_RATE', 'Interest rate period added'), { type: 'success' })

            onClose()
        } catch (err) {
            console.error(err)

            if (isServerError(err) && err.data.translationKey === 'INTEREST_RATE_PERIOD_OVERLAPS') {
                return toast(
                    t(
                        'INTEREST_RATE_PERIOD_OVERLAPS',
                        'You are trying to add a period which overlaps with an existing one',
                    ),
                    { type: 'error' },
                )
            }

            toast(t('TEXT_SOMETHING_WENT_WRONG', 'Something went wrong. Try again.'), { type: 'error' })
        }
    }

    return (
        <ModalsWrapper
            open={isOpen}
            title={t('CREATE_INTEREST_RATE_DIALOG_TITLE', 'New interest rate period')}
            handleClose={onClose}
            fullWidth
            maxWidth="sm"
            body={
                <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
                    <Grid container columnSpacing={3} rowSpacing={3}>
                        <Grid item xs={12} md={6}>
                            <ControlledMuiDateField
                                control={control}
                                name="startDate"
                                fullWidth
                                inputProps={{
                                    variant: 'standard',
                                    placeholder: t('TEXT_DATE_FROM', 'Fra'),
                                    size: 'small',
                                    autoFocus: true,
                                }}
                                format="DD.MM.YYYY"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <ControlledMuiDateField
                                control={control}
                                name="endDate"
                                fullWidth
                                inputProps={{
                                    variant: 'standard',
                                    placeholder: t('TEXT_DATE_TO', 'Til'),
                                    size: 'small',
                                }}
                                format="DD.MM.YYYY"
                                shouldDisableDate={(date) => {
                                    if (!selectedStartDate) return false

                                    const diff = date.diff(dayjs(selectedStartDate))

                                    return diff <= 0
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <ControlledMuiTextField
                                control={control}
                                name="annualInterestRatePercentage"
                                fullWidth
                                type="number"
                                placeholder={t('TEXT_INTEREST_RATE_LABEL', 'Rentesats')}
                                size="small"
                                variant="filled"
                            />
                        </Grid>
                    </Grid>
                </form>
            }
            actions={
                <ActionWrapper>
                    <Button onClick={onClose} disabled={isLoading}>
                        {t('TEXT_CANCEL')}
                    </Button>

                    <LoadingButton
                        disableElevation
                        variant="contained"
                        color="accent"
                        type="submit"
                        form={FORM_ID}
                        loading={isLoading}
                    >
                        {t('TEXT_SAVE_INTEREST_PERIOD', 'Save interest rate period')}
                    </LoadingButton>
                </ActionWrapper>
            }
        />
    )
}

const ActionWrapper = styled(Stack)(({ theme }) => ({
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}))
